"use client";

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, type RefObject } from "react";

gsap.registerPlugin(ScrollTrigger);

interface HeroParallaxRefs {
  sectionRef: RefObject<HTMLElement | null>;
  backgroundRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
}

export function useHeroParallax(
  { sectionRef, backgroundRef, contentRef }: HeroParallaxRefs,
  reduced: boolean | null
) {
  useEffect(() => {
    if (reduced) return;

    const section = sectionRef.current;
    const background = backgroundRef.current;
    const content = contentRef.current;
    if (!section || !background || !content) return;

    const ctx = gsap.context(() => {
      gsap.to(background, {
        yPercent: 14,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: "bottom top",
          scrub: 0.6,
        },
      });

      gsap.to(content, {
        yPercent: -6,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: "bottom top",
          scrub: 0.6,
        },
      });
    }, section);

    const onScroll = () => ScrollTrigger.update();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      ctx.revert();
    };
  }, [reduced, sectionRef, backgroundRef, contentRef]);
}
